import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { API_BASE } from "@/config/api";

export interface AdminUser {
  id: number;
  email: string;
}

type Status = "loading" | "authenticated" | "unauthenticated";

interface AdminSessionValue {
  status: Status;
  admin: AdminUser | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AdminSessionContext = createContext<AdminSessionValue | null>(null);

export function useAdminSession(): AdminSessionValue {
  const ctx = useContext(AdminSessionContext);
  if (!ctx) throw new Error("useAdminSession debe usarse dentro de <AdminSessionProvider>");
  return ctx;
}

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [admin, setAdmin] = useState<AdminUser | null>(null);

  const refresh = useCallback(async () => {
    setStatus("loading");
    try {
      const res = await fetch(`${API_BASE}/auth/me`);
      if (!res.ok) throw new Error("not authenticated");
      const json = (await res.json()) as AdminUser;
      setAdmin(json);
      setStatus("authenticated");
    } catch {
      setAdmin(null);
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await fetch(`${API_BASE}/auth/logout`, { method: "POST" });
    setAdmin(null);
    setStatus("unauthenticated");
  }, []);

  return (
    <AdminSessionContext.Provider value={{ status, admin, refresh, logout }}>{children}</AdminSessionContext.Provider>
  );
}
